import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import CommandPalette from '../CommandPalette';
import NotificationsDrawer from '../NotificationsDrawer';
import TrialBanner from '../TrialBanner';

const Header = ({ onMenuToggle, isSidebarOpen = false }) => {
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isHelpDropdownOpen, setIsHelpDropdownOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { profile, organization, signOut, darkMode, toggleDarkMode } = useAuth();
  const headerWrapperRef = useRef(null);

  const handleUserDropdownToggle = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
    setIsHelpDropdownOpen(false);
  };

  const handleHelpDropdownToggle = () => {
    setIsHelpDropdownOpen(!isHelpDropdownOpen);
    setIsUserDropdownOpen(false);
  };

  const handleDropdownClose = () => {
    setIsUserDropdownOpen(false);
    setIsHelpDropdownOpen(false);
  };

  const handleLogout = async () => {
    handleDropdownClose();
    await signOut();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  // Global CMD+K listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.metaKey || e?.ctrlKey) && e?.key === 'k') {
        e?.preventDefault();
        setIsCommandPaletteOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNotificationsBellClick = () => {
    setIsNotificationsOpen(o => !o);
    handleDropdownClose();
  };

  // Measure actual header height (banner + nav bar) and expose as CSS variable
  useEffect(() => {
    const el = headerWrapperRef?.current;
    if (!el) return;
    const update = () => {
      document.documentElement?.style?.setProperty('--header-height', `${el?.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  return (
    <>
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />
      <NotificationsDrawer
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        onUnreadCountChange={setUnreadCount}
      />

      <div ref={headerWrapperRef} className="fixed top-0 left-0 right-0 z-40 flex flex-col">
        <TrialBanner />
        <header className="h-16 bg-background border-b border-border">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={onMenuToggle}
                className="lg:hidden"
                aria-label="Toggle navigation menu"
              >
                <Icon name={isSidebarOpen ? "X" : "Menu"} size={20} />
              </Button>
              <div className="hidden lg:flex items-center space-x-3 ml-64">
                <img
                  src="/assets/images/wilcart_logo_1-1775846328647.png"
                  alt="Wilcart CRM Logo"
                  className="h-8 w-auto object-contain"
                />
                <span className="text-lg font-bold text-foreground">Wilcart</span>
              </div>
              <div className="flex items-center space-x-3 lg:hidden">
                <img
                  src="/assets/images/wilcart_logo_1-1775846328647.png"
                  alt="Wilcart CRM Logo"
                  className="h-8 w-auto object-contain"
                />
                <span className="text-lg font-bold text-foreground">Wilcart</span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* CMD+K Search Button */}
              <button
                onClick={() => setIsCommandPaletteOpen(true)}
                className="hidden sm:flex items-center space-x-2 px-3 py-1.5 text-sm text-muted-foreground bg-muted hover:bg-muted/80 border border-border rounded-lg transition-colors"
                aria-label="Open command palette"
              >
                <Icon name="Search" size={14} />
                <span className="text-xs">Search...</span>
                <kbd className="text-xs bg-background border border-border px-1.5 py-0.5 rounded">⌘K</kbd>
              </button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsCommandPaletteOpen(true)}
                className="hidden"
                aria-label="Search"
              >
                <Icon name="Search" size={20} />
              </Button>

              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleHelpDropdownToggle}
                  aria-label="Help and support"
                >
                  <Icon name="HelpCircle" size={20} />
                </Button>
                {isHelpDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-50" onClick={handleDropdownClose} />
                    <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-elevation-2 z-60">
                      <div className="py-1">
                        <button
                          onClick={() => { navigate('/documentation'); handleDropdownClose(); }}
                          className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                        >
                          <Icon name="Book" size={16} className="mr-3" />
                          Documentation
                        </button>
                        <button
                          onClick={() => { navigate('/contact-support'); handleDropdownClose(); }}
                          className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                        >
                          <Icon name="MessageCircle" size={16} className="mr-3" />
                          Contact Support
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Notifications Bell */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNotificationsBellClick}
                  className="relative"
                  aria-label="Notifications"
                >
                  <Icon name="Bell" size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </div>

              {/* Dark Mode Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                title={darkMode ? 'Light mode' : 'Dark mode'}
              >
                <Icon name={darkMode ? 'Sun' : 'Moon'} size={20} />
              </Button>

              <div className="relative">
                <button
                  onClick={handleUserDropdownToggle}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted transition-smooth"
                  aria-label="User account menu"
                >
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-foreground">
                      {getInitials(profile?.full_name)}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium text-foreground">{profile?.full_name || 'User'}</div>
                    <div className="text-xs text-muted-foreground capitalize">{profile?.role || 'member'}</div>
                  </div>
                  <Icon
                    name="ChevronDown"
                    size={16}
                    className={`transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isUserDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-50" onClick={handleDropdownClose} />
                    <div className="absolute right-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-elevation-2 z-60">
                      <div className="p-4 border-b border-border">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-foreground">
                              {getInitials(profile?.full_name)}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-popover-foreground">{profile?.full_name || 'User'}</div>
                            <div className="text-sm text-muted-foreground">{profile?.email || ''}</div>
                            <div className="text-xs text-muted-foreground capitalize">{profile?.role || 'member'}</div>
                          </div>
                        </div>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => { navigate('/settings'); handleDropdownClose(); }}
                          className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                        >
                          <Icon name="User" size={16} className="mr-3" />
                          Profile Settings
                        </button>
                        <button
                          onClick={() => { navigate('/settings'); handleDropdownClose(); }}
                          className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                        >
                          <Icon name="Settings" size={16} className="mr-3" />
                          Account Settings
                        </button>
                        <button
                          onClick={() => { navigate('/billing'); handleDropdownClose(); }}
                          className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                        >
                          <Icon name="CreditCard" size={16} className="mr-3" />
                          Billing & Plans
                        </button>
                        <div className="border-t border-border my-1" />
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                        >
                          <Icon name="LogOut" size={16} className="mr-3" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
      </div>
      {(isUserDropdownOpen || isHelpDropdownOpen) && (
        <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={handleDropdownClose} />
      )}
    </>
  );
};

export default Header;